import React, { useCallback, useMemo, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE, OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { EMPTY_MESSAGES } from '@/shared/lib/constants/messages';
import { formatDateTime } from '@/shared/lib/format';
import { openTagInNewTab } from '@/shared/lib/navigation';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import { highlightText } from '@/shared/lib/text/highlight';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { DrawerDomainList } from '@/shared/ui/AssetMetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import { ColumnVisibilityButton } from '@/shared/ui/ColumnVisibilityButton/ColumnVisibilityButton';
import { useColumnVisibility } from '@/shared/ui/ColumnVisibilityButton/useColumnVisibility';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { SearchInput } from '@/shared/ui/SearchInput';
import { Switch } from '@/shared/ui/Switch';
import { TableEmpty } from '@/shared/ui/TableEmpty';
import { Tooltip } from '@/shared/ui/Tooltip';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { filterEntityLabels } from '@/entities/search';

import type { DatabaseTable } from '@/features/asset-detail/api/tablesApi';
import { useTableColumnsCount } from '@/features/asset-detail/model';

import { SEARCH_PLACEHOLDERS } from '@/widgets/asset-detail/shared/constants';

import styles from './TablesTab.module.css';

const COLUMN_WIDTHS = {
  NAME: 200,
  DESCRIPTION: 244,
  COLUMN_COUNT: 120,
  DOMAINS: 244,
  TAGS: 244,
  GLOSSARY_TERMS: 244,
  UPDATED_AT: 160,
  OWNERS: 352,
} as const;

type TablesTabProps = {
  tables: DatabaseTable[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onDeletedToggle: (includeDeleted: boolean) => void;
  includeDeleted: boolean;
};

type ColumnKey = 'name' | 'description' | 'columnCount' | 'domains' | 'tags' | 'glossaryTerms' | 'updatedAt' | 'owners';

const TablesTabComponent: React.FC<TablesTabProps> = ({
  tables,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  onDeletedToggle,
  includeDeleted,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const tableFqns = useMemo(() => tables.map((table) => table.fullyQualifiedName), [tables]);
  const { countsMap, errorsMap } = useTableColumnsCount(tableFqns, includeDeleted);

  const DEFAULT_COLUMN_ORDER = useMemo<ColumnKey[]>(() => {
    const columns: ColumnKey[] = ['name', 'description', 'columnCount'];
    if (showOwners) columns.push('owners');
    columns.push('domains', 'tags', 'glossaryTerms', 'updatedAt');
    return columns;
  }, [showOwners]);

  const DEFAULT_VISIBLE_COLUMNS = useMemo<ColumnKey[]>(() => {
    return DEFAULT_COLUMN_ORDER;
  }, [DEFAULT_COLUMN_ORDER]);

  const filteredTables = useMemo(() => {
    if (!searchQuery || searchQuery.length === 0) return tables;

    const lowerQuery = searchQuery.toLowerCase();
    return tables.filter((table) => {
      const nameMatch = table.name.toLowerCase().includes(lowerQuery);
      const fqnMatch = table.fullyQualifiedName.toLowerCase().includes(lowerQuery);
      const descriptionMatch = table.description?.toLowerCase().includes(lowerQuery);

      const classificationTags = filterEntityLabels(table.tags, {
        includeOnlySource: 'Classification',
        requireConfirmed: true,
      });
      const glossaryTerms = filterEntityLabels(table.tags, {
        includeOnlySource: 'Glossary',
        requireConfirmed: true,
      });

      const tagMatch = classificationTags?.some((tag) => tag.tagFQN?.toLowerCase().includes(lowerQuery));
      const termMatch = glossaryTerms?.some((term) => term.tagFQN?.toLowerCase().includes(lowerQuery));

      return nameMatch || fqnMatch || descriptionMatch || tagMatch || termMatch;
    });
  }, [searchQuery, tables]);

  const hasData = filteredTables.length > 0;

  const allColumnsMap: Record<ColumnKey, ColumnsType<DatabaseTable>[number]> = useMemo(() => {
    const columns: Record<string, ColumnsType<DatabaseTable>[number]> = {
      name: {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        width: COLUMN_WIDTHS.NAME,
        fixed: 'left',
        render: (name: string, record: DatabaseTable) => (
          <Link to={`/table/${record.fullyQualifiedName}`}>
            {searchQuery ? highlightText(name, searchQuery, { className: 'highlight' }) : name}
          </Link>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      description: {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: hasData ? COLUMN_WIDTHS.DESCRIPTION : 'auto',
        render: (description?: string) => {
          return (
            <AssetDescription
              description={description}
              mode="expandable"
              highlightQuery={searchQuery}
              highlightClassName="highlight"
            />
          );
        },
      },
      columnCount: {
        title: '컬럼',
        dataIndex: 'columnCount',
        key: 'columnCount',
        width: COLUMN_WIDTHS.COLUMN_COUNT,
        align: 'center',
        render: (_: unknown, record: DatabaseTable) => {
          const fqn = record.fullyQualifiedName;
          const hasError = errorsMap.get(fqn);
          const count = countsMap.get(fqn);

          if (hasError) {
            return (
              <Tooltip title="컬럼 개수를 불러올 수 없습니다">
                <ExclamationCircleOutlined style={{ color: 'var(--opds-glo-palette-red-50)', fontSize: '14px' }} />
              </Tooltip>
            );
          }

          return count !== undefined ? count.toLocaleString() : '-';
        },
        sorter: (a, b) => {
          const aCount = countsMap.get(a.fullyQualifiedName) || 0;
          const bCount = countsMap.get(b.fullyQualifiedName) || 0;
          return aCount - bCount;
        },
      },
      domains: {
        title: '도메인',
        dataIndex: 'domains',
        key: 'domains',
        width: COLUMN_WIDTHS.DOMAINS,
        render: (domains: DatabaseTable['domains']) => {
          if (!domains || domains.length === 0) return '-';
          return <DrawerDomainList domains={domains} />;
        },
      },
      tags: {
        title: '태그',
        dataIndex: 'tags',
        key: 'tags',
        width: COLUMN_WIDTHS.TAGS,
        render: (tags: DatabaseTable['tags']) => {
          const classificationTags = filterEntityLabels(tags, {
            includeOnlySource: 'Classification',
          });
          if (classificationTags.length === 0) return '-';
          const badgeItems = mapTagsToBasicInfo(classificationTags);
          return <BadgeList items={badgeItems} onItemClick={openTagInNewTab} maxVisibleCount={5} />;
        },
      },
      glossaryTerms: {
        title: '용어',
        dataIndex: 'tags',
        key: 'glossaryTerms',
        width: COLUMN_WIDTHS.GLOSSARY_TERMS,
        render: (tags: DatabaseTable['tags']) => {
          const glossaryTerms = filterEntityLabels(tags, {
            includeOnlySource: 'Glossary',
            requireConfirmed: true,
          });
          if (glossaryTerms.length === 0) return '-';
          const badgeItems = glossaryTerms.map((term) => ({
            tagFQN: term.tagFQN,
            name: term.name,
            displayName: term.displayName || term.name,
          }));
          return (
            <BadgeList
              items={badgeItems}
              onItemClick={(fqn) => {
                const url = buildAssetDetailUrl('glossaryTerm', fqn);
                navigate(url);
              }}
              iconName="book"
              maxVisibleCount={5}
            />
          );
        },
      },
      updatedAt: {
        title: '업데이트 날짜',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: COLUMN_WIDTHS.UPDATED_AT,
        render: (updatedAt?: string | number) => {
          if (!updatedAt) return '-';
          return formatDateTime(updatedAt);
        },
        sorter: (a, b) => {
          if (!a.updatedAt) return 1;
          if (!b.updatedAt) return -1;
          const aTime = typeof a.updatedAt === 'string' ? dayjs(a.updatedAt).valueOf() : a.updatedAt;
          const bTime = typeof b.updatedAt === 'string' ? dayjs(b.updatedAt).valueOf() : b.updatedAt;
          return aTime - bTime;
        },
      },
    };

    if (showOwners) {
      columns.owners = {
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: COLUMN_WIDTHS.OWNERS,
        render: (owners: DatabaseTable['owners']) => {
          if (!owners || owners.length === 0) return '-';
          return <OwnerAvatars owners={owners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      };
    }

    return columns as Record<ColumnKey, ColumnsType<DatabaseTable>[number]>;
  }, [showOwners, hasData, countsMap, errorsMap, navigate, searchQuery]);

  const allColumnsMapForVisibility = useMemo(
    () =>
      Object.entries(allColumnsMap).reduce(
        (acc, [key, col]) => ({
          ...acc,
          [key]: { title: col.title as string },
        }),
        {} as Record<ColumnKey, { title: string }>
      ),
    [allColumnsMap]
  );

  const { state } = useColumnVisibility<ColumnKey>({
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    allColumnsMap: allColumnsMapForVisibility,
  });

  const { columnOrder, visibleColumns } = state;

  const visibleColumnsData = useMemo<ColumnsType<DatabaseTable>>(() => {
    return columnOrder
      .filter((key) => Object.hasOwn(allColumnsMap, key) && visibleColumns.includes(key))
      .map((key) => allColumnsMap[key]);
  }, [columnOrder, visibleColumns, allColumnsMap]);

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredTables.length,
    viewportOffset: 648,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="테이블 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.tablesTab}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.TABLES}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className={styles.toolbarRight}>
          <div className={styles.deletedToggle}>
            <Switch checked={includeDeleted} onChange={onDeletedToggle} aria-label="삭제됨 포함" color="standard" />
            <span className={styles.toggleLabel}>삭제됨</span>
          </div>
          <ColumnVisibilityButton<ColumnKey>
            defaultColumnOrder={DEFAULT_COLUMN_ORDER}
            defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
            allColumnsMap={allColumnsMapForVisibility}
            columnOrderParamKey="columnOrder"
            visibleColumnsParamKey="columns"
          />
        </div>
      </div>
      <Table
        columns={visibleColumnsData}
        dataSource={visibleColumnsData.length === 0 ? [] : filteredTables}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={paginationConfig}
        scroll={{ x: filteredTables.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              isVisibleColumns={visibleColumnsData.length === 0}
              description={searchQuery ? undefined : EMPTY_MESSAGES.NO_TABLE_ITEMS}
            />
          ),
        }}
        className={styles.tablesTable}
      />
    </div>
  );
};

export const TablesTab = React.memo(TablesTabComponent);
